import React, { useEffect, useMemo, useState, useRef, useContext, useCallback } from 'react';
import _, { uniq } from 'lodash';
import { Global, css } from '@emotion/react';
import GoogleMap, { DirectionsRenderer, InfoWindow } from '@/components/GoogleMap';
import { genAttributes } from '@onward-delivery/core';
import FTLStopHelpers from '@/utilities/FTLStopHelpers';
import { ROUTE_COLORS, SELECTED_COLOR, SCHEDULED_UNASSIGNED_COLOR } from '../constants';
import MapStopTooltip from './MapStopTooltip';
import MarkerCluster from './MarkerCluster';
import MarkerClusterGroups from './MarkerClusterGroups';
import { StartSVG, EndSVG, DefaultSVG as DropoffSVG, PickupSVG, OnwardMarker, applyMarkerProps } from './Marker';
import {
    getShape,
    drawSVG,
    getStopShape,
    useShapeFieldTypeOptions,
    useFieldShapesSingle,
    useComboPotentialChoices,
} from '../hooks/shapes';
import { useNetworkRoutesQuery, useCallbacks, useLazyRecommendations } from '../hooks';
import useShiftLasso from '../hooks/useShiftLasso';
import { PlanningDataContext, PlanningFilterContext, PlanningSelectionContext, PlanningUiContext } from '../context';
import { deliveryTypeCode } from '../util/deliveryTypeCode';
import { MarkerLegendControl } from '@/components/GoogleMap/controls/MarkerLegendControl';
import { LassoControl } from '@/components/GoogleMap/controls/LassoControl';
import { ClearOrderControl } from '@/components/GoogleMap/controls/ClearOrderControl';
import { HideRoutedOrdersControl } from '@/components/GoogleMap/controls/HideRoutedOrdersControl';
import { useClientUser } from '@/hooks';
import { ShapeControl } from '@/components/GoogleMap/controls/ShapeControl';
import { MODALS } from '../constants';
import { RecommendationsControl } from '@/components/GoogleMap/controls/RecommendationsControl';
import { getNonSkippedStops } from '../util/getNonSkippedStops';
import { colors } from '@/styles';
import MarkerStyleHolder from '../util/MarkerStyleHolder';
import { addVIPBoxToSVG, stopHasVIPOrder, VIP_BOX_HEIGHT } from './vipMarker';
import { collectPinLabels, groupPinLabelsByPosition, positionKey } from './pinLabelBox';
const PICKUP_FLAG = 'plan-page-order-pickups';
const MARKER_CLUSTERING_FLAG = 'plan-page-marker-clustering';
const DEFAULT_CENTER = { lat: 39.74, lng: -104.99 };
const DEFAULT_ZOOM = 13;
const TOOLTIP_OPTIONS = {
    minWidth: 300,
    maxWidth: 700,
    disableAutoPan: true,
    pixelOffset: new window.google.maps.Size(0, -5),
    headerDisabled: true,
};
import {
    PLAN_PAGE_LOADBOARD,
    ROUTE_CUSTOM_MAP_MARKERS,
    PRESCHEDULED_HIGHLIGHT,
    SHOW_REC_EQUIPMENT_SHAPE_TAGS,
} from '@/constants/featureFlags';

const PlanningMap = () => {
    const {
        selectedRoute,
        selectedOrders,
        setSelectedOrders,
        expandedOrders,
        setExpandedOrders,
        selectMultiOrders,
        selectedLoads,
        selectMultiLoads,
    } = useContext(PlanningSelectionContext);
    const {
        showAllDirections,
        showLoadBoardLoads,
        showNetworkRoutes,
        hideRoutedOrders,
        toggleHideRoutedOrders,
        setActionableRoute,
        setActionableStop,
        setModalOpen,
    } = useContext(PlanningUiContext);
    const {
        state: { orders, routes, loadBoardLoads },
    } = useContext(PlanningDataContext);
    const { filteredOrders } = useContext(PlanningFilterContext);

    const {
        circles,
        user_id,
        shipping_partners,
        payment_type,
        savedShapes,
        tags,
        locations,
        invoiceAccess,
        dispatch_settings,
    } = useClientUser();

    // Custom shape flags
    const routeCustomShapeMapMarkersEnabled = circles?.[ROUTE_CUSTOM_MAP_MARKERS];
    const showRecEquipmentShapeTags = circles?.[SHOW_REC_EQUIPMENT_SHAPE_TAGS];

    const { networkRoutes } = useNetworkRoutesQuery();
    const { saveShapes } = useCallbacks();

    const mapRef = useRef();
    // Selected-route stop pins, key -> { marker, ...paint state }. Pins are
    // retained across selection clicks and repainted in place.
    const routePinsRef = useRef(new Map());
    // Unassigned/route-stop markers, key -> { marker, sig }. Instances are
    // retained so selection flips only setOptions the markers that changed.
    const orderMarkersRef = useRef(new Map());
    // Current descriptor per marker key, for event dispatchers on retained markers.
    const descriptorsByKeyRef = useRef({});

    const [lassoEnabled, setLasso] = useState(false);
    const shiftLasso = useShiftLasso(mapRef);
    const [mapData, setMapData] = useState({});
    const [loadMapData, setLoadMapData] = useState({});
    const [networkMapData, setNetworkMapData] = useState({});
    const [selectedMarker, selectMarker] = useState(null);
    const [selectedStop, selectStop] = useState(null);
    const [selectedOrder, selectOrder] = useState(null);
    const [selectedLoad, selectLoad] = useState(null);
    const [shapesOverride, setShapesOverride] = useState([]);
    const [comboShapesOverride, setComboShapesOverride] = useState([]);
    const [shapeFieldType, setShapeFieldType] = useState('item_type');
    const [markerType, setMarkerType] = useState('service_type');
    const [lockShapeType, setLockShapeType] = useState(false);
    const [showComboShapes, setShowComboShapes] = useState(false);

    const onSelectBound = useCallback(
        (bounds) => {
            // Hit-test the same filtered set the markers are drawn from, not the
            // full `orders` dataset. Otherwise the lasso grabs orders that were
            // never rendered — other shippers' orders and anything the category,
            // assignment, or search filters hid.
            const selected = filteredOrders.filter((order) => {
                const { lat, long } = genAttributes(order);
                const { lat: pulat, long: pulong } = genAttributes(order, true);

                return order.crossdock_leg === 'pickup'
                    ? bounds.contains({ lat: order[pulat], lng: order[pulong] })
                    : bounds.contains({ lat: order[lat], lng: order[long] });
            });
            setSelectedOrders((prev) => {
                const rowId = (order) => `${order.order_id}_${order.crossdock_leg}`;
                const someSelected = selected.some((order) => prev[rowId(order)]);
                const allSelected = selected.every((order) => prev[rowId(order)]);
                if (allSelected) {
                    return {
                        ...prev,
                        ...Object.fromEntries(selected.map((order) => [rowId(order), false])),
                    };
                } else if (someSelected) {
                    return {
                        ...prev,
                        ...Object.fromEntries(selected.map((order) => [rowId(order), true])),
                    };
                } else {
                    return {
                        ...prev,
                        ...Object.fromEntries(selected.map((order) => [rowId(order), !prev[rowId(order)]])),
                    };
                }
            });
        },
        [filteredOrders]
    );

    const {
        getOrderRecommendations,
        data: recommendations,
        loading: recommendationsLoading,
    } = useLazyRecommendations();

    const routesByID = useMemo(() => {
        return Object.fromEntries(routes.map((route) => [route.route_id, route]));
    }, [routes]);

    const routeIndexByID = useMemo(() => {
        return Object.fromEntries(routes.map((route, idx) => [route.route_id, idx]));
    }, [routes]);

    const ordersByID = useMemo(() => {
        return Object.fromEntries(orders.map((order) => [order.order_id, order]));
    }, [orders]);

    const ordersByRowId = useMemo(() => {
        return Object.fromEntries(orders.map((order) => [`${order.order_id}_${order.crossdock_leg}`, order]));
    }, [orders]);

    // Marker instances outlive the closures that created them, so their event
    // handlers read the latest interaction state through this ref instead of
    // closing over props/state.
    const interactionRef = useRef({});
    interactionRef.current = {
        selectedOrders,
        expandedOrders,
        selectMultiOrders,
        selectMultiLoads,
        setModalOpen,
        setActionableRoute,
        setActionableStop,
        ordersByID,
        selectedRoute,
    };

    const shapeFieldTypeOptions = useShapeFieldTypeOptions(orders, user_id, showRecEquipmentShapeTags);

    useEffect(() => {
        if (!lockShapeType) {
            setShapeFieldType(shapeFieldTypeOptions[0] || 'item_type');
        }
    }, [shapeFieldTypeOptions]);

    // Pins persist across data broadcasts, so the style-pool bookkeeping must
    // share their lifetime; deselection frees slots via turnMarkerStyleOff.
    const svgStyles = useMemo(() => new MarkerStyleHolder(), []);

    const loadsByID = useMemo(() => {
        if (!loadBoardLoads) return {};
        return Object.fromEntries(loadBoardLoads?.map((order) => [order.order_id, order]));
    }, [loadBoardLoads]);

    const fieldShapesSingle = useFieldShapesSingle({
        orders,
        savedShapes,
        shapeFieldType,
        showComboShapes,
        shipping_partners,
        user_id,
        includeRecommendedEquipment: showRecEquipmentShapeTags,
    });

    const comboPotentialChoices = useComboPotentialChoices(orders, showRecEquipmentShapeTags);

    useEffect(() => {
        setShapesOverride(fieldShapesSingle);
        setComboShapesOverride(savedShapes?.combinations || null);
    }, [fieldShapesSingle, savedShapes]);

    const tooltip = useMemo(() => {
        if (selectedStop) {
            const orders = (selectedStop.orders || []).map((key) => ordersByID[key]).filter((o) => o);

            return (
                <MapStopTooltip
                    orders={orders}
                    stop={selectedStop}
                    user_id={user_id}
                    dispatch_settings={dispatch_settings}
                    invoiceAccess={invoiceAccess}
                    circles={circles}
                />
            );
        }

        if (selectedOrder) {
            const orders = selectedOrder.map((key) => ordersByRowId[key]).filter((o) => o);
            return (
                <MapStopTooltip
                    orders={orders}
                    user_id={user_id}
                    dispatch_settings={dispatch_settings}
                    invoiceAccess={invoiceAccess}
                    circles={circles}
                />
            );
        }
        if (selectedLoad) {
            const orders = selectedLoad.map((key) => loadsByID[key]).filter((o) => o);
            return (
                <MapStopTooltip
                    orders={orders}
                    user_id={user_id}
                    dispatch_settings={dispatch_settings}
                    invoiceAccess={invoiceAccess}
                    circles={circles}
                />
            );
        }

        return null;
    }, [selectedStop, selectedRoute, selectedOrder, selectedLoad, ordersByID, loadsByID, ordersByRowId, circles]);

    const routesToRender = useMemo(() => {
        return routes.filter((route) => {
            const selected = route.route_id === selectedRoute;

            return showAllDirections && !selected && mapData[route.route_id];
        });
    }, [routes, mapData, showAllDirections, selectedRoute]);

    const networkRoutesToRender = useMemo(() => {
        return networkRoutes.filter((route) => {
            return showNetworkRoutes && networkMapData[route.route_id];
        });
    }, [networkRoutes, networkMapData, showNetworkRoutes]);

    const remainingOrders = useMemo(() => {
        // Unassigned markers on the map. Read filteredOrders so search, the
        // Assignment filter, and the category combo boxes all apply to the
        // markers in the same way they apply to the sidebar list.
        const rendered = routesToRender.map((route) => route.route_id);

        return filteredOrders.filter((order) => {
            const orderRouteId = order.crossdock_leg === 'pickup' ? order.pickup_route_id : order.dropoff_route_id;
            if (!rendered.includes(orderRouteId) && !(selectedRoute && orderRouteId === selectedRoute)) {
                if (hideRoutedOrders && (order.pickup_route_id || order.dropoff_route_id)) {
                    return false;
                }
                return true;
            }
            return false;
        });
    }, [filteredOrders, routesToRender, selectedRoute, hideRoutedOrders]);

    // Stop sequences for the rendered routes, computed directly from route data.
    // Bounds (and the directions fetch) read from here instead of mapData so the
    // viewport fits once per route/selection change instead of re-fitting on
    // every Google Directions arrival.
    const routeStopSequences = useMemo(() => {
        return Object.fromEntries(
            routes
                .filter(({ route_id }) => showAllDirections || route_id === selectedRoute)
                .map((route) => [
                    route.route_id,
                    getNonSkippedStops(FTLStopHelpers.getStopSequence(route.stopsByRouteId)),
                ])
        );
    }, [routes, selectedRoute, showAllDirections]);

    // Auto-fit tracks the OVERALL plan dataset, not the selected route. The
    // viewport re-fits when the displayed orders change (date, filters, search,
    // orders entering/leaving the plan) but stays put when a route or stop is
    // edited: removing a stop or route only reassigns orders, it does not change
    // the set of order locations on the board, so the bounding box — and the
    // fit — is identical. Points are sorted so an incidental reordering of
    // filteredOrders can't retrigger a fit to the same box.
    const bounds = useMemo(() => {
        const points = (filteredOrders || [])
            .map((order) => {
                const { lat, long } = genAttributes(order, order.crossdock_leg === 'pickup');
                return { lat: order[lat], lng: order[long] };
            })
            .filter((c) => c.lat && c.lng)
            .sort((a, b) => a.lat - b.lat || a.lng - b.lng);

        if (points.length) {
            return points;
        }

        // Backstop: with no orders on the board, fit to the client's saved
        // locations so the map still opens somewhere sensible.
        if (locations?.length) {
            return locations
                .filter((l) => l.lat && l.lng)
                .map((l) => ({
                    lat: l.lat,
                    lng: l.lng,
                }));
        }

        return [];
    }, [filteredOrders, locations]);

    const useBounds = useMemo(() => {
        return bounds && bounds.length > 0;
    }, [bounds]);

    const getDirections = async (route, stopSequence) => {
        if (route && route?.stopsByRouteId?.length) {
            if (!stopSequence.length) return { routeId: route.route_id };
            const routeDirections = await FTLStopHelpers.getGMapsDirections(stopSequence, new Date(route.start_time));
            if (_.isObject(routeDirections) && Object.keys(routeDirections).length) {
                return {
                    routeId: route.route_id,
                    stopSequence,
                    routeDirections,
                };
            }
        }
        return { routeId: route.route_id };
    };

    // Event handlers for retained route pins: resolve the pin's current stop and
    // route through routePinsRef and the interaction state through interactionRef
    // at call time, so long-lived listeners never act on stale data.
    const makeRoutePinCallbacks = (key, type) => {
        const hover = {
            onMouseOver: (ref) => {
                const entry = routePinsRef.current.get(key);
                selectStop(entry ? entry.stop : null);
                selectOrder(null);
                selectMarker(ref);
            },
            onMouseOut: () => {
                selectStop(null);
                selectOrder(null);
                selectMarker(null);
            },
        };

        if (type !== 'do') {
            return hover;
        }

        return {
            onContextMenu: () => {
                const entry = routePinsRef.current.get(key);
                if (!entry) {
                    return;
                }
                const { setModalOpen, setActionableRoute, setActionableStop } = interactionRef.current;
                setModalOpen(MODALS.ROUTE_REMOVE_STOP);
                setActionableRoute(entry.route);
                setActionableStop({ ...entry.stop, stopOrders: entry.stop.orders });
            },
            onClick: () => {
                const entry = routePinsRef.current.get(key);
                if (!entry) {
                    return;
                }
                const { selectMultiOrders, selectedOrders, ordersByID } = interactionRef.current;
                const rowIds = entry.stop.orders?.map(
                    (o) => `${ordersByID[o].order_id}_${ordersByID[o].crossdock_leg}`
                );
                selectMultiOrders(rowIds || [], !rowIds?.every((o) => selectedOrders[o]));
            },
            ...hover,
        };
    };

    // Rebuild the selected route's stop pins only when the route/stop data
    // itself changes. Selection clicks repaint retained pins in the effect
    // below instead of tearing everything down.
    useEffect(() => {
        routePinsRef.current.forEach(({ marker }) => {
            marker.setMap(null);
        });
        routePinsRef.current = new Map();

        if (mapRef.current) {
            const selectedOrdersNow = interactionRef.current.selectedOrders;
            const pins = routes
                .filter((r) => r.route_id === selectedRoute)
                .reduce((markers, route) => {
                    const idx = routes.findIndex((r) => r.route_id === route.route_id);
                    const routeStops = (routeStopSequences[route.route_id] || []).map((stop) => ({
                        ...stop,
                        shape: getStopShape(
                            stop,
                            ordersByID,
                            shapeFieldType,
                            shapesOverride,
                            showRecEquipmentShapeTags
                        ),
                    }));
                    const color = route.route_color ? route.route_color : idx >= 0 ? ROUTE_COLORS[idx] : '#000';

                    return [
                        ...markers,
                        ...routeStops
                            .map((stop) => {
                                let type;
                                let svg;
                                let label = ' ';
                                let shape = undefined;
                                let hasVIPOrder = false;
                                let isSelected = false;

                                if (stop.start) {
                                    type = 'start';
                                    svg = StartSVG({ color });
                                } else if (stop.end) {
                                    type = 'end';
                                    svg = EndSVG({ color });
                                } else if (
                                    stop.type === 'DROPOFF' ||
                                    (stop.type === 'PICKUP' && stop.returns?.length)
                                ) {
                                    type = 'do';
                                    label = `${stop.ordering}`;
                                    isSelected = !!stop.orders?.every((o) => selectedOrdersNow[o]);

                                    if (routeCustomShapeMapMarkersEnabled) {
                                        if (isSelected) {
                                            svgStyles.addMarkerAndGetStyle(stop, routeStops);
                                        } else {
                                            svgStyles.turnMarkerStyleOff(stop);
                                        }
                                        svg = drawSVG(stop.shape, { color: isSelected ? SELECTED_COLOR : color });
                                        shape = stop.shape;
                                    } else if (isSelected) {
                                        const styleFunction = svgStyles.addMarkerAndGetStyle(stop, routeStops);
                                        svg = styleFunction({
                                            color: SELECTED_COLOR,
                                            altColor: color,
                                        });
                                    } else {
                                        svgStyles.turnMarkerStyleOff(stop);
                                        svg = DropoffSVG({
                                            color: color,
                                        });
                                    }

                                    // Add VIP svg if order on stop is tagged VIP
                                    hasVIPOrder = stopHasVIPOrder(stop, ordersByID);
                                    if (hasVIPOrder) {
                                        svg = addVIPBoxToSVG(svg);
                                    }
                                } else if (stop.type === 'PICKUP' && !stop.returns?.length) {
                                    if (hideRoutedOrders && route.route_id !== selectedRoute) {
                                        return null;
                                    }
                                    type = 'pu';
                                    isSelected = !!stop.orders?.some((o) => selectedOrdersNow[o]);
                                    if (isSelected) {
                                        const styleFunction = svgStyles.addAndGetPickupMarkerStyle(stop);
                                        svg = styleFunction({
                                            color: SELECTED_COLOR,
                                            altColor: color,
                                        });
                                    } else {
                                        svg = PickupSVG({
                                            color: color,
                                        });
                                    }
                                }

                                if (!type) {
                                    return null;
                                }

                                const key = `${route.route_id}-${type}-${stop.ordering}`;

                                return {
                                    key,
                                    type,
                                    label,
                                    position: { lat: stop.lat, lng: stop.lng },
                                    svg,
                                    labelYOffset: hasVIPOrder ? VIP_BOX_HEIGHT : 0,
                                    shape,
                                    // Only dropoff stops. A pickup pin stands for
                                    // every order leaving one shipper location, so
                                    // its box would be an unreadable pile.
                                    pinLabels:
                                        type === 'do'
                                            ? collectPinLabels((stop.orders || []).map((id) => ordersByID[id]))
                                            : [],
                                    callbacks: type === 'do' || type === 'pu' ? makeRoutePinCallbacks(key, type) : {},
                                    stop,
                                    route,
                                    routeStops,
                                    color,
                                    isSelected,
                                };
                            })
                            .filter(Boolean),
                    ];
                }, []);

            const overlaps = pins.reduce((acc, marker) => {
                const { lat, lng } = marker.position;

                acc[lat] = { ...acc[lat], [lng]: [...(acc?.[lat]?.[lng] || []), marker.key] };

                return acc;
            }, {});

            // Stops sharing a coordinate pool their box onto the first pin
            // there, as the unassigned markers below do.
            const grouped = groupPinLabelsByPosition(pins);

            pins.forEach((props) => {
                const overlapIdx = overlaps[props.position.lat][props.position.lng].indexOf(props.key);
                const pinLabels = overlapIdx === 0 ? grouped[positionKey(props.position)]?.pinLabels || [] : [];
                const marker = new OnwardMarker({
                    key: props.key,
                    position: props.position,
                    label: props.label,
                    svg: props.svg,
                    shape: props.shape,
                    labelYOffset: props.labelYOffset,
                    pinLabels,
                    callbacks: props.callbacks,
                    overlapIdx,
                    map: mapRef.current,
                });

                // Two stops can transiently share an ordering (optimistic
                // reorder window), colliding on the key; detach the displaced
                // marker or no later teardown can reach it.
                const displaced = routePinsRef.current.get(props.key);
                if (displaced && displaced.marker !== marker) {
                    displaced.marker.setMap(null);
                }

                routePinsRef.current.set(props.key, {
                    marker,
                    type: props.type,
                    stop: props.stop,
                    route: props.route,
                    routeStops: props.routeStops,
                    color: props.color,
                    label: props.label,
                    position: props.position,
                    overlapIdx,
                    // The grouped set, not the descriptor's own — the selection
                    // repaint below reuses this and must not resurrect a box on
                    // a pin that pooled its labels onto another.
                    pinLabels,
                    isSelected: props.isSelected,
                });
            });
        }
        // ordersByID is read above but deliberately not a dependency. Routed
        // rows are built from data.routes, which selects each order's full
        // OrderFields (pin_labels and tags included), so an edit to any order on
        // a route already arrives as a new `routes`. Listing it would only add
        // rebuilds for broadcasts that touch no route.
    }, [mapRef.current, routes, selectedRoute, routeStopSequences, shapesOverride, shapeFieldType, hideRoutedOrders]);

    // Repaint retained route pins whose selection state flipped; unaffected pins
    // are left untouched so unrelated selection clicks cause no map churn.
    useEffect(() => {
        routePinsRef.current.forEach((entry, key) => {
            if (entry.type !== 'do' && entry.type !== 'pu') {
                return;
            }

            const isSelected =
                entry.type === 'do'
                    ? !!entry.stop.orders?.every((o) => selectedOrders[o])
                    : !!entry.stop.orders?.some((o) => selectedOrders[o]);

            if (isSelected === entry.isSelected) {
                return;
            }

            let svg;
            let shape = undefined;
            let labelYOffset = 0;

            if (entry.type === 'do') {
                if (routeCustomShapeMapMarkersEnabled) {
                    if (isSelected) {
                        svgStyles.addMarkerAndGetStyle(entry.stop, entry.routeStops);
                    } else {
                        svgStyles.turnMarkerStyleOff(entry.stop);
                    }
                    svg = drawSVG(entry.stop.shape, { color: isSelected ? SELECTED_COLOR : entry.color });
                    shape = entry.stop.shape;
                } else if (isSelected) {
                    const styleFunction = svgStyles.addMarkerAndGetStyle(entry.stop, entry.routeStops);
                    svg = styleFunction({
                        color: SELECTED_COLOR,
                        altColor: entry.color,
                    });
                } else {
                    svgStyles.turnMarkerStyleOff(entry.stop);
                    svg = DropoffSVG({
                        color: entry.color,
                    });
                }

                if (stopHasVIPOrder(entry.stop, interactionRef.current.ordersByID)) {
                    svg = addVIPBoxToSVG(svg);
                    labelYOffset = VIP_BOX_HEIGHT;
                }
            } else if (isSelected) {
                const styleFunction = svgStyles.addAndGetPickupMarkerStyle(entry.stop);
                svg = styleFunction({
                    color: SELECTED_COLOR,
                    altColor: entry.color,
                });
            } else {
                svg = PickupSVG({
                    color: entry.color,
                });
            }

            applyMarkerProps(entry.marker, {
                key,
                position: entry.position,
                label: entry.label,
                svg,
                shape,
                overlapIdx: entry.overlapIdx,
                labelYOffset,
                pinLabels: entry.pinLabels,
            });
            entry.isSelected = isSelected;
        });
    }, [selectedOrders]);

    // Selection-independent marker descriptors. baseColor is the non-selected
    // color; svgFactory produces the exact SVG rendered today for a given color.
    // Selection/expansion flips are painted onto retained marker instances in
    // the memo below without rebuilding descriptors.
    const unassignedDescriptors = useMemo(() => {
        const raw = {
            dropoff: remainingOrders.reduce((omAcc, order) => {
                const rowId = `${order.order_id}_${order.crossdock_leg}`;
                const routeId = order.crossdock_leg === 'pickup' ? order.pickup_route_id : order.dropoff_route_id;
                const { lat, long } = genAttributes(order);
                const warehouse = order.wh_events?.findLast((e) => e.action.endsWith('ADD_CD'))?.location || {};
                const markerCoordinates =
                    order.crossdock_leg === 'pickup'
                        ? { lat: warehouse.lat, lng: warehouse.lng }
                        : { lat: order[lat], lng: order[long] };

                if (!markerCoordinates.lat || !markerCoordinates.lng) {
                    return omAcc;
                }

                const markerKey = `${order.shipper_id}-${rowId}-do`;
                const shapeInfoSingle = getShape(shapeFieldType, shapesOverride, order, showRecEquipmentShapeTags);
                let shapeInfoCombo;
                if (savedShapes?.combinations) {
                    savedShapes.combinations.forEach((combination) => {
                        const isMatch = Object.keys(combination).every((key) => {
                            if (key !== 'shape') {
                                return order[key] === combination[key];
                            }
                            return true;
                        });
                        if (isMatch) {
                            shapeInfoCombo = combination.shape;
                        }
                    });
                }
                const orderRouteColor = routes.find((r) => r.route_id === routeId)?.route_color;

                if (omAcc[markerKey]) {
                    return {
                        ...omAcc,
                        [markerKey]: {
                            ...omAcc[markerKey],
                            orderKeys: [...omAcc[markerKey].orderKeys, rowId],
                        },
                    };
                } else {
                    return {
                        ...omAcc,
                        [markerKey]: {
                            orderKeys: [rowId],
                            color:
                                order.prescheduled && circles?.[PRESCHEDULED_HIGHLIGHT]
                                    ? SCHEDULED_UNASSIGNED_COLOR
                                    : orderRouteColor
                                    ? orderRouteColor
                                    : ROUTE_COLORS[routes.findIndex((r) => r.route_id === routeId)] ||
                                      (order.delivery_date ? SCHEDULED_UNASSIGNED_COLOR : 'black'),
                            position: markerCoordinates,
                            key: markerKey,
                            shape: showComboShapes ? shapeInfoCombo || null : shapeInfoSingle?.shape || null,
                        },
                    };
                }
            }, {}),
            pickup: remainingOrders.reduce((acc, order) => {
                const rowId = `${order.order_id}_${order.crossdock_leg}`;
                const { lat, long } = genAttributes(order, true);
                const warehouse = order.wh_events?.findLast((e) => e.action.endsWith('ADD_CD'))?.location || {};
                const markerCoordinates =
                    order.crossdock_leg === 'dropoff'
                        ? { lat: warehouse.lat, lng: warehouse.lng }
                        : { lat: order[lat], lng: order[long] };

                if (!markerCoordinates.lat || !markerCoordinates.lng) {
                    return acc;
                }

                const markerKey = `${order.shipper_id}-${markerCoordinates.lat}-${markerCoordinates.lng}-pu`;

                if (acc[markerKey]) {
                    return {
                        ...acc,
                        [markerKey]: {
                            ...acc[markerKey],
                            orderKeys: [...acc[markerKey].orderKeys, rowId],
                            color: acc[markerKey]?.color || (order.pickup_date ? SCHEDULED_UNASSIGNED_COLOR : 'black'),
                        },
                    };
                } else {
                    return {
                        ...acc,
                        [markerKey]: {
                            orderKeys: [rowId],
                            color: order.pickup_date ? SCHEDULED_UNASSIGNED_COLOR : 'black',
                            position: markerCoordinates,
                            key: markerKey,
                        },
                    };
                }
            }, {}),
        };

        const markers = {
            dropoff: Object.entries(raw.dropoff).map(([key, orderMarker]) => {
                const codes = orderMarker?.orderKeys.map((key) => {
                    const order = ordersByRowId[key];
                    const { location_type } = genAttributes(order);

                    if (markerType === 'service_type') {
                        return order[location_type];
                    } else if (markerType === 'order_type') {
                        return order.order_type;
                    }

                    return null;
                });

                const isUnscheduled = orderMarker?.orderKeys.every((key) => {
                    const order = ordersByRowId[key];
                    return order.crossdock_leg === 'pickup' ? !order.pickup_date : !order.delivery_date;
                });

                const label = isUnscheduled ? 'U' : deliveryTypeCode(codes, markerType);
                const vipTag = 'vip';
                let hasVIP = false;
                const tags = uniq(
                    (orderMarker?.orderKeys || []).reduce((acc, key) => {
                        const orderTags = (ordersByRowId[key]?.tags || [])
                            .filter(({ tag }) => {
                                if (tag.tag?.toLowerCase() === vipTag) {
                                    hasVIP = true;
                                }
                                return tag.route_tag;
                            })
                            .map(({ tag }) => tag.tag);
                        return [...acc, ...orderTags];
                    }, [])
                );

                const markerShape = orderMarker.shape;
                const markerHasVIP = hasVIP;

                return {
                    orders: orderMarker.orderKeys,
                    key,
                    label,
                    tags: markerType === 'order_tags' ? tags : [],
                    pinLabels: collectPinLabels((orderMarker?.orderKeys || []).map((k) => ordersByRowId[k])),
                    position: orderMarker.position,
                    shape: orderMarker.shape,
                    labelYOffset: hasVIP ? VIP_BOX_HEIGHT : 0,
                    baseColor: orderMarker.color,
                    recolorOnSelect: true,
                    svgFactory: (color) => {
                        const baseSvg = drawSVG(markerShape, { color });
                        return markerHasVIP ? addVIPBoxToSVG(baseSvg) : baseSvg;
                    },
                    callbacks: {
                        onClick: () => {
                            const { selectMultiOrders, selectMultiLoads, selectedOrders } = interactionRef.current;
                            const orderKeys = descriptorsByKeyRef.current[key]?.orders || [];
                            selectMultiOrders(orderKeys, !orderKeys.every((k) => selectedOrders[k]));
                            selectMultiLoads(null);
                            selectLoad(null);
                        },
                        onMouseOver: (ref) => {
                            selectStop(null);
                            selectOrder(descriptorsByKeyRef.current[key]?.orders || null);
                            selectMarker(ref);
                        },
                        onMouseOut: () => {
                            selectStop(null);
                            selectOrder(null);
                            selectMarker(null);
                        },
                    },
                };
            }),
            pickup: Object.entries(raw.pickup).map(([key, orderMarker]) => {
                return {
                    orders: orderMarker.orderKeys,
                    key,
                    position: orderMarker.position,
                    baseColor: orderMarker.color,
                    recolorOnSelect: true,
                    svgFactory: (color) => PickupSVG({ color }),
                    callbacks: {},
                };
            }),
        };

        const routeStops = routesToRender.reduce((markers, route) => {
            const idx = routes.findIndex((r) => r.route_id === route.route_id);
            const stops = (routeStopSequences[route.route_id] || []).map((stop) => ({
                ...stop,
                shape: getStopShape(stop, ordersByID, shapeFieldType, shapesOverride, showRecEquipmentShapeTags),
            }));
            const color = route.route_color ? route.route_color : idx >= 0 ? ROUTE_COLORS[idx] : '#000';

            return [
                ...markers,
                ...stops
                    .map((stop) => {
                        let type;
                        let svgFactory;
                        let label = ' ';
                        let hasVIPOrder = false;

                        if (stop.start) {
                            type = 'start';
                            svgFactory = (svgColor) => StartSVG({ color: svgColor });
                        } else if (stop.end) {
                            type = 'end';
                            svgFactory = (svgColor) => EndSVG({ color: svgColor });
                        } else if (stop.type === 'DROPOFF' || (stop.type === 'PICKUP' && stop.returns?.length)) {
                            type = 'do';
                            label = `${stop.ordering}`;
                            hasVIPOrder = stopHasVIPOrder(stop, ordersByID);

                            const stopShape = stop.shape;
                            const stopHasVIP = hasVIPOrder;
                            svgFactory = (svgColor) => {
                                const svg = routeCustomShapeMapMarkersEnabled
                                    ? drawSVG(stopShape, { color: svgColor })
                                    : DropoffSVG({ color: svgColor });
                                return stopHasVIP ? addVIPBoxToSVG(svg) : svg;
                            };
                        } else if (stop.type === 'PICKUP' && !stop.returns?.length) {
                            if (hideRoutedOrders && route.route_id !== selectedRoute) {
                                return null;
                            }
                            type = 'pu';
                            svgFactory = (svgColor) => PickupSVG({ color: svgColor });
                        }

                        if (!type) {
                            return null;
                        }

                        const key = `${route.route_id}-${type}-${stop.ordering}`;

                        let callbacks = {};
                        if (type === 'do') {
                            callbacks = {
                                onContextMenu: () => {
                                    const descriptor = descriptorsByKeyRef.current[key];
                                    if (!descriptor) {
                                        return;
                                    }
                                    const { setModalOpen, setActionableRoute, setActionableStop } =
                                        interactionRef.current;
                                    setModalOpen(MODALS.ROUTE_REMOVE_STOP);
                                    setActionableRoute(descriptor.route);
                                    setActionableStop({ ...descriptor.stop, stopOrders: descriptor.stop.orders });
                                },
                                onClick: () => {
                                    // Route-stop click selection is intentionally disabled.
                                },
                                onMouseOver: (ref) => {
                                    const descriptor = descriptorsByKeyRef.current[key];
                                    selectStop(descriptor ? descriptor.stop : null);
                                    selectOrder(null);
                                    selectMarker(ref);
                                },
                                onMouseOut: () => {
                                    selectStop(null);
                                    selectOrder(null);
                                    selectMarker(null);
                                },
                            };
                        } else if (type === 'pu') {
                            callbacks = {
                                onMouseOver: (ref) => {
                                    const descriptor = descriptorsByKeyRef.current[key];
                                    selectStop(descriptor ? descriptor.stop : null);
                                    selectOrder(null);
                                    selectMarker(ref);
                                },
                                onMouseOut: () => {
                                    selectStop(null);
                                    selectOrder(null);
                                    selectMarker(null);
                                },
                            };
                        }

                        return {
                            orders: stop.orders,
                            key,
                            label,
                            // Dropoff stops only — see the matching note on the
                            // route-pin path above.
                            pinLabels:
                                type === 'do' ? collectPinLabels((stop.orders || []).map((id) => ordersByID[id])) : [],
                            position: { lat: stop.lat, lng: stop.lng },
                            labelYOffset: hasVIPOrder ? VIP_BOX_HEIGHT : 0,
                            shape: stop.shape,
                            baseColor: color,
                            svgFactory,
                            callbacks,
                            stop,
                            route,
                        };
                    })
                    .filter(Boolean),
            ];
        }, []);

        const all = [...markers.dropoff, ...(circles?.[PICKUP_FLAG] ? markers.pickup : []), ...routeStops];

        descriptorsByKeyRef.current = Object.fromEntries(all.map((descriptor) => [descriptor.key, descriptor]));

        return all;
        // ordersByID/ordersByRowId: read for stop shapes and pin labels. Both
        // track `orders`, as remainingOrders already does, so listing them adds
        // no extra recomputes — it just stops the route-stop descriptors from
        // relying on the unassigned list happening to change at the same time.
    }, [
        remainingOrders,
        routesToRender,
        routeStopSequences,
        circles,
        shapesOverride,
        markerType,
        hideRoutedOrders,
        ordersByID,
        ordersByRowId,
    ]);

    const [regularMarkers, selectedMarkers, expandedMarkers] = useMemo(() => {
        const all = unassignedDescriptors;

        const overlaps = all.reduce((acc, marker) => {
            const { lat, lng } = marker.position;

            acc[lat] = { ...acc[lat], [lng]: [...(acc?.[lat]?.[lng] || []), marker.key] };

            return acc;
        }, {});

        // Markers are keyed per order, so several orders at one address are
        // several pins. Pool their box contents by coordinate and let only the
        // first pin there render it, or each hangs its own box and they stack
        // on the same pixels with only the topmost readable.
        const grouped = groupPinLabelsByPosition(all);

        const cache = orderMarkersRef.current;
        const seen = new Set();

        // Create-if-absent / setOptions-if-changed; idempotent so descriptors
        // appearing in more than one bucket (or a re-invoked memo) are safe.
        const paint = (descriptor) => {
            const overlapIdx = overlaps[descriptor.position.lat][descriptor.position.lng].indexOf(descriptor.key);
            const isSelected = (descriptor.orders || []).some((id) => selectedOrders[id]);
            const svg = descriptor.svgFactory(
                isSelected && descriptor.recolorOnSelect ? SELECTED_COLOR : descriptor.baseColor
            );

            const group = grouped[positionKey(descriptor.position)] || { tags: [], pinLabels: [] };
            const tags = overlapIdx === 0 ? group.tags : [];
            const pinLabels = overlapIdx === 0 ? group.pinLabels : [];

            const sig = [
                svg,
                isSelected,
                overlapIdx,
                `${descriptor.position.lat},${descriptor.position.lng}`,
                descriptor.label,
                tags.join(','),
                pinLabels.join(','),
                descriptor.labelYOffset,
            ].join(':');

            const props = {
                key: descriptor.key,
                position: descriptor.position,
                label: descriptor.label,
                tags,
                pinLabels,
                svg,
                shape: descriptor.shape,
                overlapIdx,
                labelYOffset: descriptor.labelYOffset,
            };

            let entry = cache.get(descriptor.key);
            if (!entry) {
                entry = {
                    marker: new OnwardMarker({
                        ...props,
                        orders: descriptor.orders,
                        callbacks: descriptor.callbacks,
                    }),
                    sig,
                };
                cache.set(descriptor.key, entry);
            } else if (entry.sig !== sig) {
                applyMarkerProps(entry.marker, props);
                entry.sig = sig;
            }

            // Cluster click handlers read order ids off the marker instance.
            entry.marker.ids = descriptor.orders;
            seen.add(descriptor.key);

            return entry.marker;
        };

        const selected = all.filter((marker) => (marker?.orders || []).every((id) => selectedOrders[id]));
        const regular = all.filter(
            (marker) => !(marker?.orders || []).every((id) => selectedOrders[id] || expandedOrders[id])
        );
        const expanded = all.filter((marker) =>
            (marker?.orders || []).every((id) => expandedOrders[id] && !selectedOrders[id])
        );

        const buckets = [regular.map(paint), selected.map(paint), expanded.map(paint)];

        [...cache.entries()].forEach(([key, entry]) => {
            if (!seen.has(key)) {
                entry.marker.setMap(null);
                cache.delete(key);
            }
        });

        return buckets;
    }, [unassignedDescriptors, selectedOrders, expandedOrders]);

    const loadMarkers = useMemo(() => {
        if (!circles?.[PLAN_PAGE_LOADBOARD] || !showLoadBoardLoads) {
            return [];
        }

        const raw = {
            dropoff: loadBoardLoads.reduce((omAcc, order) => {
                const { lat, long } = genAttributes(order);
                const markerCoordinates = { lat: order[lat], lng: order[long] };
                const markerKey = `${order.shipper_id}-${order.order_id}-do`;

                if (omAcc[markerKey]) {
                    return {
                        ...omAcc,
                        [markerKey]: {
                            ...omAcc[markerKey],
                            orderKeys: [...omAcc[markerKey].orderKeys, order.order_id],
                        },
                    };
                } else {
                    return {
                        ...omAcc,
                        [markerKey]: {
                            orderKeys: [order.order_id],
                            color: selectedLoads[order.order_id] ? SELECTED_COLOR : 'darkgreen',
                            position: markerCoordinates,
                            key: markerKey,
                        },
                    };
                }
            }, {}),
            pickup: loadBoardLoads.reduce((acc, order) => {
                const { lat, long } = genAttributes(order, true);
                const markerCoordinates = { lat: order[lat], lng: order[long] };
                const markerKey = `${order.shipper_id}-${order.order_id}-pu`;

                if (acc[markerKey]) {
                    return {
                        ...acc,
                        [markerKey]: {
                            ...acc[markerKey],
                            orderKeys: [...acc[markerKey].orderKeys, order.order_id],
                            color: [...acc[markerKey].orderKeys, order.order_id]?.some(
                                (order_id) => selectedLoads[order_id]
                            )
                                ? SELECTED_COLOR
                                : 'darkgreen',
                        },
                    };
                } else {
                    return {
                        ...acc,
                        [markerKey]: {
                            orderKeys: [order.order_id],
                            color: selectedLoads[order.order_id] ? SELECTED_COLOR : 'darkgreen',
                            position: markerCoordinates,
                            key: markerKey,
                        },
                    };
                }
            }, {}),
        };

        const markers = {
            dropoff: Object.entries(raw.dropoff).map(([key, loadMarker]) => {
                return {
                    key,
                    label: deliveryTypeCode(['marketPlace'], 'service_type'),
                    position: loadMarker.position,
                    svg: DropoffSVG({ color: loadMarker.color }),
                    callbacks: {
                        onClick: () => {
                            selectMultiLoads(
                                loadMarker.orderKeys,
                                !loadMarker.orderKeys?.every((key) => selectedLoads[key])
                            );
                            selectMultiOrders(null);
                            selectOrder(null);
                        },
                        onMouseOver: (ref) => {
                            selectStop(null);
                            selectLoad(loadMarker.orderKeys);
                            selectOrder(null);
                            selectMarker(ref);
                        },
                        onMouseOut: () => {
                            selectStop(null);
                            selectLoad(null);
                            selectOrder(null);
                            selectMarker(null);
                        },
                    },
                };
            }),
            pickup: Object.entries(raw.pickup).map(([key, loadMarker], idx) => {
                return {
                    key,
                    position: loadMarker.position,
                    svg: PickupSVG({ color: loadMarker.color }),
                    callbacks: {},
                };
            }),
        };

        const all = [...markers.dropoff, ...markers.pickup];
        const overlaps = all.reduce((acc, marker) => {
            const { lat, lng } = marker.position;

            acc[lat] = { ...acc[lat], [lng]: [...(acc?.[lat]?.[lng] || []), marker.key] };

            return acc;
        }, {});

        return all.map((props) => {
            const idx = overlaps[props.position.lat][props.position.lng].indexOf(props.key);
            return new OnwardMarker({ ...props, overlapIdx: idx });
        });
    }, [loadBoardLoads, selectedLoads, showLoadBoardLoads]);

    const networkMarkers = useMemo(() => {
        const routeStops = networkRoutesToRender.reduce((markers, route) => {
            const stops = networkMapData[route.route_id]?.stopSequence || [];
            const color = 'midnightblue';

            return [
                ...markers,
                ...stops.map((stop) => {
                    let type;
                    let callbacks = {};
                    let svg;
                    let label = ' ';

                    if (stop.start) {
                        type = 'start';
                        svg = StartSVG({ color });
                    } else if (stop.end) {
                        type = 'end';
                        svg = EndSVG({ color });
                    }

                    return {
                        key: `${route.route_id}-${type}-${stop.ordering}`,
                        label,
                        position: { lat: stop.lat, lng: stop.lng },
                        svg,
                        callbacks,
                    };
                }),
            ];
        }, []);

        const all = routeStops;
        const overlaps = all.reduce((acc, marker) => {
            const { lat, lng } = marker.position;

            acc[lat] = { ...acc[lat], [lng]: [...(acc?.[lat]?.[lng] || []), marker.key] };

            return acc;
        }, {});

        return all.map((props) => {
            const idx = overlaps[props.position.lat][props.position.lng].indexOf(props.key);
            return new OnwardMarker({ ...props, overlapIdx: idx });
        });
    }, [networkRoutesToRender, networkMapData]);

    // Batch directions arrivals into one mapData merge per ~100ms window, and
    // track in-flight requests so re-runs triggered by each merge neither redo
    // stop-sequence work (routeStopSequences is memoized) nor re-issue fetches.
    const pendingMapDataRef = useRef({ results: {}, timeout: null });
    const inFlightDirectionsRef = useRef(new Set());

    useEffect(() => {
        return () => {
            if (pendingMapDataRef.current.timeout) {
                clearTimeout(pendingMapDataRef.current.timeout);
            }
        };
    }, []);

    useEffect(() => {
        if (routes) {
            const rendered = routes.filter(({ route_id }) => showAllDirections || route_id === selectedRoute);

            rendered.forEach((route) => {
                const routeStops = routeStopSequences[route.route_id] || [];
                if (routeStops.length === 0 && mapData[route.route_id]) {
                    setMapData((prev) => {
                        const { [route.route_id]: removed, ...remaining } = prev;

                        return remaining;
                    });
                } else if (
                    routeStops.length > 0 &&
                    (mapData[route.route_id]?.stopSequence?.length !== routeStops.length ||
                        !mapData[route.route_id]?.stopSequence?.every(
                            (stop, i) => stop.address === routeStops[i].address
                        ))
                ) {
                    const signature = `${route.route_id}:${routeStops.map((stop) => stop.address).join('|')}`;
                    if (inFlightDirectionsRef.current.has(signature)) {
                        return;
                    }
                    inFlightDirectionsRef.current.add(signature);

                    getDirections(route, routeStops)
                        .then((result) => {
                            inFlightDirectionsRef.current.delete(signature);

                            if (result.routeDirections) {
                                const pending = pendingMapDataRef.current;
                                pending.results[result.routeId] = {
                                    stopSequence: result.stopSequence,
                                    routeDirections: result.routeDirections,
                                };

                                if (!pending.timeout) {
                                    pending.timeout = setTimeout(() => {
                                        const flushed = pending.results;
                                        pending.results = {};
                                        pending.timeout = null;
                                        setMapData((prev) => ({ ...prev, ...flushed }));
                                    }, 100);
                                }
                            }
                        })
                        .catch((err) => {
                            // Free the signature so the next effect run can retry.
                            inFlightDirectionsRef.current.delete(signature);
                            console.error(err);
                        });
                }
            });
        }
    }, [routes, mapData, selectedRoute, showAllDirections, routeStopSequences]);

    useEffect(() => {
        if (networkRoutes && showNetworkRoutes) {
            networkRoutes.forEach((route) => {
                const routeStops = getNonSkippedStops(FTLStopHelpers.getStopSequence(route.stopsByRouteId));
                if (routeStops.length === 0 && networkMapData[route.route_id]) {
                    setNetworkMapData((prev) => {
                        const { [route.route_id]: removed, ...remaining } = prev;

                        return remaining;
                    });
                } else if (
                    routeStops.length > 0 &&
                    (networkMapData[route.route_id]?.stopSequence?.length !== routeStops.length ||
                        !networkMapData[route.route_id]?.stopSequence?.every(
                            (stop, i) => stop.address === routeStops[i].address
                        ))
                ) {
                    getDirections(route, routeStops).then((result) => {
                        if (result.routeDirections) {
                            setNetworkMapData((prev) => ({
                                ...prev,
                                [result.routeId]: {
                                    stopSequence: result.stopSequence,
                                    routeDirections: result.routeDirections,
                                },
                            }));
                        }
                    });
                }
            });
        }
    }, [networkRoutes, networkMapData, showNetworkRoutes]);

    useEffect(() => {
        if (loadBoardLoads) {
            loadBoardLoads.forEach((load) => {
                if (loadMapData[load.order_id]) {
                    return;
                } else {
                    FTLStopHelpers.getLoadDirections(load).then((result) => {
                        if (result.status === 'OK') {
                            setLoadMapData((prev) => ({
                                ...prev,
                                [load.order_id]: {
                                    directions: result,
                                },
                            }));
                        }
                    });
                }
            });
        }
    }, [loadBoardLoads, loadMapData]);

    const renderDirections = (routeId, routeDirections, color) => {
        return (
            <DirectionsRenderer
                key={`directions-${routeId}`}
                directions={routeDirections}
                options={{
                    suppressMarkers: true,
                    polylineOptions: {
                        strokeColor: color ? color : ROUTE_COLORS[routeIndexByID[routeId]] || 'black',
                        strokeOpacity: 1,
                    },
                }}
            />
        );
    };

    return (
        <>
            <Global
                styles={css`
                    .gm-style .gm-style-iw-c {
                        & button[title='Close'] {
                            visibility: hidden;
                        }
                    }
                `}
            />
            <GoogleMap
                defaultMap={!!circles?.['plan-page-default-map']}
                ref={mapRef}
                style={{ height: '100%' }}
                bounds={bounds}
                center={!useBounds ? DEFAULT_CENTER : undefined}
                zoom={!useBounds ? DEFAULT_ZOOM : undefined}
            >
                {/* Directions for Selected Route */}
                {Object.keys(mapData).length && selectedRoute && (
                    <>
                        {renderDirections(
                            selectedRoute,
                            mapData[selectedRoute]?.routeDirections,
                            routesByID[selectedRoute]?.route_color
                        )}
                    </>
                )}

                {/* Directions for all routes if none selected */}
                {Object.keys(mapData).length && !selectedRoute && showAllDirections && (
                    <>
                        {routesToRender.map((route) =>
                            renderDirections(
                                route.route_id,
                                mapData[route.route_id].routeDirections || [],
                                route.route_color
                            )
                        )}
                    </>
                )}
                {Object.keys(networkMapData).length && showNetworkRoutes && (
                    <>
                        {networkRoutesToRender.map(({ route_id }) =>
                            renderDirections(route_id, networkMapData[route_id].routeDirections || [], 'midnightblue')
                        )}
                    </>
                )}

                {loadBoardLoads?.length && circles?.[PLAN_PAGE_LOADBOARD] && showLoadBoardLoads && (
                    <>
                        {loadBoardLoads.map((load) =>
                            renderDirections(load.order_id, loadMapData[load.order_id]?.directions || [], '#71b77a')
                        )}
                    </>
                )}
            </GoogleMap>

            <MarkerClusterGroups
                map={mapRef}
                regular={{
                    markers: regularMarkers,
                    disabled: !circles?.[MARKER_CLUSTERING_FLAG] || regularMarkers?.length <= 100,
                    onClusterClick: (e, cluster, map) => {
                        const ids = cluster.markers.reduce((acc, marker) => {
                            return [...acc, ...marker.ids];
                        }, []);

                        setExpandedOrders((prev) => {
                            const select = !ids.every((o) => prev[o]);

                            return {
                                ...prev,
                                ...Object.fromEntries(ids.map((id) => [id, select])),
                            };
                        });
                    },
                }}
                expanded={{
                    markers: expandedMarkers,
                    disabled: !circles?.[MARKER_CLUSTERING_FLAG] || expandedMarkers?.length,
                    onClusterClick: (e, cluster, map) => {
                        const ids = cluster.markers.reduce((acc, marker) => {
                            return [...acc, ...marker.ids];
                        }, []);
                        setSelectedOrders((prev) => {
                            const select = !ids.every((o) => prev[o]);
                            return {
                                ...prev,
                                ...Object.fromEntries(ids.map((id) => [id, select])),
                            };
                        });
                    },
                }}
                selected={{
                    markers: selectedMarkers,
                    disabled: !circles?.[MARKER_CLUSTERING_FLAG] || selectedMarkers?.length,
                    color: colors.greens.primary,
                    onClusterClick: (e, cluster, map) => {
                        const ids = cluster.markers.reduce((acc, marker) => {
                            return [...acc, ...marker.ids];
                        }, []);
                        setSelectedOrders((prev) => {
                            const select = !ids.every((o) => prev[o]);
                            return {
                                ...prev,
                                ...Object.fromEntries(ids.map((id) => [id, select])),
                            };
                        });
                    },
                }}
            />
            <MarkerCluster
                map={mapRef}
                markers={loadMarkers}
                disabled={!circles?.[MARKER_CLUSTERING_FLAG] || loadMarkers?.length <= 100}
                color="darkgreen"
                callbacks={{}}
            />
            <MarkerCluster
                map={mapRef}
                markers={networkMarkers}
                disabled={!circles?.[MARKER_CLUSTERING_FLAG] || networkMarkers?.length <= 100}
                color="midnightblue"
                callbacks={{}}
            />

            {circles?.['plan-page-recommendations'] && (
                <RecommendationsControl
                    mapRef={mapRef}
                    recommendations={recommendations}
                    loading={recommendationsLoading}
                    callbacks={{
                        getRecommendations: getOrderRecommendations,
                        selectRecommendation: (order_id) => {
                            selectMultiLoads([order_id]);
                        },
                    }}
                />
            )}
            <LassoControl
                mapRef={mapRef}
                isEnabled={lassoEnabled || shiftLasso}
                callbacks={{
                    onSelect: onSelectBound,
                    toggleLasso: () => {
                        setLasso((prev) => !prev);
                    },
                }}
            />
            <ClearOrderControl
                mapRef={mapRef}
                callbacks={{
                    onClick: () => {
                        setSelectedOrders({});
                    },
                }}
            />
            <HideRoutedOrdersControl
                mapRef={mapRef}
                hideRoutedOrders={hideRoutedOrders}
                callbacks={{
                    onToggle: toggleHideRoutedOrders,
                }}
            />
            <MarkerLegendControl
                mapRef={mapRef}
                type={markerType}
                callbacks={{
                    setMarkerType,
                }}
            />
            {shapeFieldTypeOptions.length && (
                <ShapeControl
                    mapRef={mapRef}
                    saveShapes={saveShapes}
                    savedShapes={savedShapes}
                    shapesOverride={shapesOverride}
                    setShapesOverride={setShapesOverride}
                    showComboShapes={showComboShapes}
                    setShowComboShapes={setShowComboShapes}
                    shapeFieldType={shapeFieldType}
                    setShapeFieldType={setShapeFieldType}
                    setLockShapeType={setLockShapeType}
                    shapeFieldTypeOptions={shapeFieldTypeOptions}
                    comboPotentialChoices={comboPotentialChoices}
                    setComboShapesOverride={setComboShapesOverride}
                    comboShapesOverride={comboShapesOverride}
                />
            )}

            <InfoWindow content={tooltip} map={mapRef} marker={selectedMarker} opts={TOOLTIP_OPTIONS} />
        </>
    );
};

export default PlanningMap;
